import React, { useState } from "react";
import ColorSettings from "./ColorSettings";
import TeamNameSettings from "./TeamNameSettings";
import ImageSettings from "./ImageSettings";
import AnalyticsSettings from "./AnalyticsSettings";
import SeoSettings from "./SeoSettings";
import ContactSettings from "./ContactSettings";
import SocialLinksSettings from "./SocialLinksSettings";
import axios from "axios";
import SuccessAlert from '../helpers/successAlert'

const Settings = ({ initialSettings, t_controls }) => {
  const [colors, setColors] = useState({
    main_color: initialSettings.main_color,
    second_color: initialSettings.second_color,
  });
  const [teamName, setTeamName] = useState(initialSettings.team_name);
  const [teamLogo, setTeamLogo] = useState(null)
  const [pageFavicon, setPageFavicon] = useState(null)
  const [ga4MeasurementId, setGa4MeasurementId] = useState(initialSettings.ga4_measurement_id || "");
  const [seoData, setSeoData] = useState({
    team_city: initialSettings.team_city || "",
    team_country: initialSettings.team_country || "",
    team_league: initialSettings.team_league || "",
    site_name: initialSettings.seo_site_name || {},
    keywords: initialSettings.seo_keywords || {},
    meta_description: initialSettings.seo_meta_description || {}
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [showGoalsAgainst, setShowGoalsAgainst] = useState(!!initialSettings.statistics_show_goals_against);
  const [showPenaltyMinutes, setShowPenaltyMinutes] = useState(!!initialSettings.statistics_show_penalty_minutes);
  const [tradingCardsEnabled, setTradingCardsEnabled] = useState(!!initialSettings.trading_cards_enabled);
  const [googleDriveApiKey, setGoogleDriveApiKey] = useState(initialSettings.google_drive_api_key || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [contactData, setContactData] = useState({
    contact_name: initialSettings.contact_name || "",
    contact_telephone: initialSettings.contact_telephone || "",
    contact_email: initialSettings.contact_email || "",
    footer_contact_email: initialSettings.footer_contact_email || "",
  });
  const [socialsData, setSocialsData] = useState(initialSettings.socials || {});

  const handleSave = () => {
    const token = document.querySelector("[name=csrf-token]").content;
    const locale = window.location.pathname.split("/")[1];
    const settingsUrl = `/${locale}/settings`;

    setIsLoading(true);
  
    const formData = new FormData();
    formData.append("main_color", colors.main_color);
    formData.append("second_color", colors.second_color);
    formData.append("team_name", teamName);
    if (teamLogo) {
      formData.append("team_logo", teamLogo); 
    }
    if (pageFavicon) {
      formData.append("page_favicon", pageFavicon); 
    }
    formData.append("ga4_measurement_id", ga4MeasurementId || "");

    // Team location settings
    formData.append("team_city", seoData.team_city || "");
    formData.append("team_country", seoData.team_country || "");
    formData.append("team_league", seoData.team_league || "");

    // SEO settings per locale (dynamically for all available locales)
    const availableLocales = initialSettings.available_locales || ['en', 'lt', 'ru'];
    availableLocales.forEach(locale => {
      formData.append(`seo_site_name_${locale}`, (seoData.site_name && seoData.site_name[locale]) || "");
      formData.append(`seo_keywords_${locale}`, (seoData.keywords && seoData.keywords[locale]) || "");
      formData.append(`seo_meta_description_${locale}`, (seoData.meta_description && seoData.meta_description[locale]) || "");
    });
    
    formData.append("statistics_show_goals_against", showGoalsAgainst ? "true" : "false");
    formData.append("statistics_show_penalty_minutes", showPenaltyMinutes ? "true" : "false");
    formData.append("trading_cards_enabled", tradingCardsEnabled ? "true" : "false");
    formData.append("google_drive_api_key", googleDriveApiKey || "");

    // Contact info
    formData.append("contact_name", contactData.contact_name || "");
    formData.append("contact_telephone", contactData.contact_telephone || "");
    formData.append("contact_email", contactData.contact_email || "");
    formData.append("footer_contact_email", contactData.footer_contact_email || "");

    // Social links (sent as socials[key]=url)
    Object.entries(socialsData).forEach(([k, v]) => {
      if (v) formData.append("socials[" + k + "]", v);
    });

    axios
      .patch(settingsUrl, formData, {
        headers: {
          "X-CSRF-TOKEN": token,
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        setIsLoading(false);
        setAlertMessage(t_controls.updated);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Failed to save settings:", error);
      });
  };

  const handleReloadRemoteConfig = () => {
    const token = document.querySelector("[name=csrf-token]").content;
    const locale = window.location.pathname.split("/")[1];
    const reloadUrl = `/${locale}/settings/reload_remote_config`;

    setIsReloading(true);

    axios
      .post(reloadUrl, {}, {
        headers: {
          "X-CSRF-TOKEN": token,
        },
      })
      .then(() => {
        setIsReloading(false);
        // Toast surfaced by the axios response interceptor.
        // Reload page to reflect new settings.
        setTimeout(() => window.location.reload(), 1500);
      })
      .catch(() => {
        setIsReloading(false);
        // Toast surfaced by the axios response interceptor.
      });
  };

  return (
    <div className="container mt-2">
      {alertMessage && <SuccessAlert message={alertMessage} />}

      <ColorSettings
        colors={colors}
        setColors={setColors}
        t_controls={t_controls}
      />

      <TeamNameSettings
        teamName={teamName}
        setTeamName={setTeamName}
        t_controls={t_controls}
      />

      <ImageSettings
        setImageFile={setTeamLogo}
        initParam={initialSettings.team_logo}
        t_controls={t_controls}
        t_control_label={t_controls.team_logo}
      />

      <ImageSettings
        setImageFile={setPageFavicon}
        initParam={initialSettings.page_favicon}
        t_controls={t_controls}
        t_control_label={t_controls.page_favicon}
      />

      <AnalyticsSettings
        ga4MeasurementId={ga4MeasurementId}
        setGa4MeasurementId={setGa4MeasurementId}
        t_controls={t_controls}
      />

      <SeoSettings
        seoData={seoData}
        setSeoData={setSeoData}
        t_controls={t_controls}
        availableLocales={initialSettings.available_locales || ['en', 'lt', 'ru']}
      />

      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title">Google Drive Integration</h5>
          <p className="text-muted small">
            Configure Google Drive API key to enable gallery albums from Google Drive.
            If you already have Google Sign-In configured, you can use the same Google Cloud project.
          </p>
          <div className="form-group">
            <label htmlFor="googleDriveApiKey">Google Drive API Key</label>
            <input
              type="text"
              className="form-control"
              id="googleDriveApiKey"
              value={googleDriveApiKey}
              onChange={(e) => setGoogleDriveApiKey(e.target.value)}
              placeholder="Paste your API key here (or leave empty to use ENV variable)"
            />
            <small className="form-text text-muted">
              Get your API key from Google Cloud Console → Credentials. If using environment variable, set GOOGLE_DRIVE_API_KEY.
            </small>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title">{t_controls.stats && t_controls.stats.title ? t_controls.stats.title : 'Statistics'}</h5>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showGoalsAgainst"
              checked={showGoalsAgainst}
              onChange={(e) => setShowGoalsAgainst(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showGoalsAgainst">
              {t_controls.show_goals_against || 'Show "Goals against" column in team statistics'}
            </label>
          </div>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="showPenaltyMinutes"
              checked={showPenaltyMinutes}
              onChange={(e) => setShowPenaltyMinutes(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showPenaltyMinutes">
              {t_controls.show_penalty_minutes || 'Show "Penalty minutes" column in team statistics'}
            </label>
          </div>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="tradingCardsEnabled"
              checked={tradingCardsEnabled}
              onChange={(e) => setTradingCardsEnabled(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="tradingCardsEnabled">
              {t_controls.trading_cards_enabled || 'Use trading card design for the roster page'}
            </label>
          </div>
          {tradingCardsEnabled && (
            <a
              className="d-inline-block mt-2"
              href={`/${window.location.pathname.split("/")[1]}/settings/card_scoring`}
            >
              {(t_controls.card_scoring && t_controls.card_scoring.configure_link) ||
                "Configure card scoring →"}
            </a>
          )}
        </div>
      </div>

      {initialSettings.remote_config_available && (
        <div className="card mt-3">
          <div className="card-body">
            <h5 className="card-title">{t_controls.reset_config_title || "Reset Configuration"}</h5>
            <p className="text-muted small">
              {t_controls.reset_config_description || "Reset all settings to the initial configuration provided during setup. This will overwrite any changes you have made."}
            </p>
            <button
              className="btn btn-warning"
              disabled={isReloading}
              onClick={handleReloadRemoteConfig}
            >
              {isReloading ? (t_controls.resetting || "Resetting...") : (t_controls.reset_to_initial || "Reset to Initial Config")}
            </button>
          </div>
        </div>
      )}

      <ContactSettings
        contactData={contactData}
        setContactData={setContactData}
        t_controls={t_controls}
      />

      <SocialLinksSettings
        socialsData={socialsData}
        setSocialsData={setSocialsData}
        t_controls={t_controls}
      />

            <div className="form-buttons">
        <button
          className="btn btn-primary"
          disabled={!teamName.trim() || isLoading}
          onClick={handleSave}
        >
          {isLoading ? (t_controls.saving || "Saving...") : t_controls.save}
        </button>
      </div>
    </div>
  );
};

export default Settings;
